import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CancelIcon from '@material-ui/icons/Cancel';
import { cancelSubscription, getSuscriptions } from '../services/payments';

const useStyles = makeStyles(theme => ({
  table: {
    marginTop: 10,
    padding: 5,
    '& .MuiTableCell-sizeSmall': {
      padding: theme.spacing(0.75),
    },
    '& td': {
      cursor: 'default',
      wordBreak: 'break-word',
    },
  },
  header: {
    background: theme.palette.primary.main,
  },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
    userSelect: 'none',
  },
  noResultsBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    textAlign: 'center',
  },
  filtersBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  formControl: { minWidth: 160 },
  grow: { flex: 1 },
  rowGreen: { backgroundColor: '#e8f5e9' },
  rowYellow: { backgroundColor: '#fffde7' },
  rowRed: { backgroundColor: '#ffebee' },
}));

const ORDER_OPTIONS = [
  { value: 'email', label: 'Email (A-Z)' },
  { value: '-email', label: 'Email (Z-A)' },
  { value: 'created_at', label: 'Creación (asc)' },
  { value: '-created_at', label: 'Creación (desc)' },
];

const STATUS_OPTIONS = ['', 'authorized', 'cancelled', 'pending'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// —— Utils
const safeDate = (raw) => {
  if (!raw) return '-';
  const d = new Date(raw);
  return isNaN(d) ? String(raw) : d.toLocaleString('es-AR', { hour12: false });
};

const formatMoney = (amount, currencyId) => {
  if (amount == null) return '-';
  try {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: currencyId || 'ARS' }).format(Number(amount));
  } catch (_) {
    return `${currencyId || ''} ${Number(amount).toFixed(2)}`.trim();
  }
};

const rowClassBySemaphore = (classes, sem) => {
  const s = (sem || '').toLowerCase();
  if (s === 'green') return classes.rowGreen;
  if (s === 'yellow') return classes.rowYellow;
  if (s === 'red') return classes.rowRed;
  return '';
};

export default function SubscriptionsTable() {
  const classes = useStyles();
  const initialFilters = { status: '', email: '', ordering: 'email'};

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [showFilters, setShowFilters] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [filters, setFilters] = useState(initialFilters);

  const [selected, setSelected] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [openCancel, setOpenCancel] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = useMemo(() => (count && pageSize ? Math.max(1, Math.ceil(count / pageSize)) : 0), [count, pageSize]);
  const pageOptions = useMemo(() => Array.from({ length: totalPages }, (_, i) => i + 1), [totalPages]);

  const fetchList = async (pageNumber = 1, perPage = 25) => {
    setLoading(true);
    try {

      const response = await getSuscriptions(pageNumber, perPage, filters);
      const data = (response && response.data && response.data.results) || [];

      setItems(data);
      setCount(Number(data?.count) || 0);
    } catch (err) {
      setItems([]);
      setCount(0);
      setSnack({ open: true, message: 'No se pudo obtener la lista de suscripciones.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const hadleCancelSubscription = async (reference, dev) => {
    try {
      await cancelSubscription(reference,dev);
      setSnack({ open: true, message: 'Suscripción cancelada correctamente.', severity: 'success' });
      fetchList(page, pageSize);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || 'No se pudo cancelar la suscripción.';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  useEffect(() => {
    fetchList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchList(1, pageSize);
  };
  const handleClearFilters = () => {
    setPage(1);
    fetchList(1, pageSize);
    setFilters(initialFilters);
  };

  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

  const openDetails = (row) => { setSelected(row); setOpenDetail(true); };
  const askCancel = (row) => { setCancelTarget(row); setOpenCancel(true); };
  const confirmCancel = () => {
    if (!cancelTarget) return;
    hadleCancelSubscription(cancelTarget.id, devMode);
    setOpenCancel(false);
  };

  return (
    <>
      {loading ? (
        <Box mt={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box p={4} pb={0} display="flex" alignItems="center" gap={8}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowFilters(s => !s)}
            >
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </Button>
            <Box ml={2}>
              <FormControlLabel
                control={<Switch checked={devMode} onChange={(e) => setDevMode(e.target.checked)} color="primary" />}
                label="Modo dev para cancelar"
              />
            </Box>
          </Box>

          <Collapse in={showFilters}>
            <Box p={4} pb={0}>
              <Box className={classes.filtersBar}>
                <FormControl variant="outlined" size="small">
                  <InputLabel id="status-label">Estado</InputLabel>
                  <Select
                    labelId="status-label"
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                    label="Estado"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt || 'all'} value={opt}>
                        {opt ? opt : 'Todos'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Email"
                  variant="outlined"
                  size="small"
                  value={filters.email}
                  onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
                />

                <FormControl variant="outlined" size="small">
                  <InputLabel id="order-label">Ordenar por</InputLabel>
                  <Select
                    labelId="order-label"
                    value={filters.ordering}
                    onChange={(e) => setFilters((prev) => ({ ...prev, ordering: e.target.value }))}
                    label="Ordenar por"
                  >
                    {ORDER_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box display="flex" gap={8} mb={2}>
                <Button variant="contained" color="primary" onClick={handleApplyFilters}>Aplicar filtros</Button>
                <Button onClick={handleClearFilters}>Limpiar</Button>
              </Box>
            </Box>
          </Collapse>

          <Box p={4} pt={0}>
            {items && items.length > 0 ? (
              <>
                <TableContainer component={Paper}>
                  <Table size="small" aria-label="Suscripciones" className={classes.table}>
                    <TableHead>
                      <TableRow className={classes.header}>
                        <TableCell align="center" className={classes.headerCell}>ID</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Email</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Nombre</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Estado</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Monto</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Frecuencia</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Próximo cobro</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Última modif.</TableCell>
                        <TableCell align="center" className={classes.headerCell}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map(row => {
                        const sem = row?.summarized?.semaphore;
                        const rowClass = rowClassBySemaphore(classes, sem);
                        return (
                          <TableRow key={row.id}  hover className={rowClass} >
                            <TableCell align="center" style={{cursor:'pointer'}} onClick={() => openDetails(row)}>                           
                                <Button size="small" >{row.id}</Button>                              
                            </TableCell>
                            <TableCell align="center">{row?.cliente_email || '-'}</TableCell>
                            <TableCell align="center">{row?.cliente_nombre || '-'}</TableCell>
                            <TableCell align="center">{row?.status || '-'}</TableCell>
                            <TableCell align="center">{formatMoney(row?.transaction_amount, row?.currency_id)}</TableCell>
                            <TableCell align="center">{`${row?.frequency ?? '-'} ${row?.frequency_type ?? ''}`.trim()}</TableCell>
                            <TableCell align="center">{safeDate(row?.next_payment_date)}</TableCell>
                            <TableCell align="center">{safeDate(row?.last_modified)}</TableCell>                                                   
                            <TableCell align="center">
                              <Box display="flex" justifyContent="center" gridGap={8}>                              
                                {row?.status === 'authorized' ? (
                                  <Tooltip title="Cancelar suscripción">
                                    <span>
                                      <IconButton size="small" onClick={() => askCancel(row)}>
                                        <CancelIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box className={classes.toolbar}>
                  <FormControl variant="outlined" size="small" className={classes.formControl}>
                    <InputLabel id="page-size-label">Cantidad</InputLabel>
                    <Select labelId="page-size-label" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} label="Cantidad por página">
                      {PAGE_SIZE_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" size="small" className={classes.formControl} disabled={totalPages === 0}>
                    <InputLabel id="page-label">Página</InputLabel>
                    <Select labelId="page-label" value={Math.min(page, totalPages || 1)} onChange={(e) => setPage(Number(e.target.value))} label="Página">
                      {pageOptions.map(p => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <IconButton onClick={handlePrev} disabled={page <= 1} aria-label="Anterior">
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton onClick={handleNext} disabled={page >= totalPages} aria-label="Siguiente">
                    <ChevronRightIcon />
                  </IconButton>

                  <Box className={classes.grow} />
                  <Box>Página {page} de {totalPages || 1} — Total: {count}</Box>
                </Box>
              </>
            ) : (
              <Box className={classes.noResultsBox}>No hay resultados.</Box>
            )}
          </Box>
        </>
      )}

      {/* Detalle */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de suscripción</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box component="dl" display="grid" gridTemplateColumns="1fr 1fr" gridGap={16}>
              {Object.entries(selected).map(([key, value]) => (
                <React.Fragment key={key}>
                  <Box component="dt" fontWeight="bold" color="primary.main">{key}</Box>
                  <Box component="dd" m={0} style={{ wordBreak: 'break-word' }}>
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(false)} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm cancel */}
      <Dialog open={openCancel} onClose={() => setOpenCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar suscripción</DialogTitle>
        <DialogContent dividers>
          {cancelTarget ? (
            <>
              <Box mb={2}>Vas a cancelar la suscripción <b>{cancelTarget.id}</b> del email <b>{cancelTarget.cliente_email}</b>.</Box>
              <FormControlLabel control={<Switch checked={devMode} onChange={(e) => setDevMode(e.target.checked)} color="primary" />} label="Modo dev" />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancel(false)}>Cancelar</Button>
          <Button onClick={confirmCancel} color="secondary" variant="contained" startIcon={<CancelIcon />}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        onClose={() => setSnack({ ...snack, open: false })}
        autoHideDuration={4000}
        message={snack.message}
      />
    </>
  );
}
